'use client'

import { ReactNode, useEffect, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import i18n from '@/config/i18n'
import { useLang } from '@/store/useLanguageStore'
import { isSupportedLang, Lang } from '@/utils/localization/language'

// 하이드레이션 동안 쓰는 ko 고정 인스턴스. 전역 i18n은 헤더 등에서 마운트 즉시 언어가 바뀌는데,
// 그때 아직 하이드레이트 안 된 Suspense 경계가 서버 HTML(ko)과 다른 언어로 그려지면 React #418이 난다.
const hydrationI18n = i18n.cloneInstance({ lng: 'ko' })

const getBrowserLang = (): Lang => {
  const browserLang = navigator.language
  if (browserLang.startsWith('zh-Hans')) return 'zh-CN'
  if (browserLang.startsWith('zh-Hant')) return 'zh-TW'
  if (browserLang.startsWith('ja') || browserLang.startsWith('jp')) return 'jp'
  if (browserLang.startsWith('en')) return 'en'
  return 'ko'
}

// 렌더를 막지 않는다: 크롤러가 받는 정적 HTML에 ko 본문이 있어야 한다.
// 마운트 후 ?locale= → 쿠키 → 브라우저 언어 순으로 결정해 전환한다 (ko 외 사용자는 잠깐 ko가 보일 수 있음).
export default function I18nProvider({ children }: { children: ReactNode }) {
  const { setLang } = useLang()
  const [instance, setInstance] = useState(hydrationI18n)

  useEffect(() => {
    const localeParam = new URLSearchParams(window.location.search).get('locale')
    const cookieLang = document.cookie.match(/lang=([^;]+)/)?.[1]
    const lang: Lang = isSupportedLang(localeParam)
      ? localeParam
      : isSupportedLang(cookieLang)
        ? cookieLang
        : getBrowserLang()

    setLang(lang) // 쿠키도 같이 저장한다
    // context 교체는 React 상태 업데이트라, 남은 경계는 React가 먼저 ko로 하이드레이트한 뒤 반영한다
    i18n.changeLanguage(lang).then(() => setInstance(i18n))
  }, [setLang])

  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>
}
